import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ARTICLE_URL = "https://www.hey-lets-travel.com/community/eastenderz-hi-ibiza-2026-east-end-dubs";
const JOSH_BAKER_URL = "/events/josh-baker-youme-amnesia-ibiza-2-july-2026";
const CONCIERGE_URL = "/concierge";
const EVENTS_URL = "/events";

const KEY_DATES = [
  "2 June 2026 - Opening Party with East End Dubs B2B Skepta, SOSA B2B Hot Since 82, Locklead and Cristina Lazic.",
  "9 June 2026 - East End Dubs, Patrick Topping, Wheats and Stef Davidse.",
  "30 June 2026 - East End Dubs B2B Jamie Jones, Jamback and Kitty Hall.",
  "7 July 2026 - East End Dubs, Franky Rizardo, Gaskin and Ellia Jaya.",
  "28 July 2026 - East End Dubs, Skream, TSHA and Ryan Resso.",
  "11 August 2026 - East End Dubs, Kolter, Dennis Quin B2B Robbie Doherty and Kellie Allen.",
  "18 August 2026 - East End Dubs, SOSA, Silvie Loto and Janeret.",
  "15 September 2026 - East End Dubs B2B Marco Carola, Gaskin and Lola Palmer.",
  "29 September 2026 - East End Dubs, Hot Since 82, Oden & Fatzo live and Gallya.",
  "6 October 2026 - Closing Party with East End Dubs B2B Ewan McVicar, Traumer B2B Priku and A For Alpha.",
];

const FAQS = [
  {
    question: "When is Eastenderz at Hi Ibiza in 2026?",
    answer: "Eastenderz runs every Tuesday at Hi Ibiza from 2 June to 6 October 2026.",
  },
  {
    question: "Who is behind Eastenderz?",
    answer: "Eastenderz is led by East End Dubs, the London-based DJ, producer and creative force behind the Eastenderz label and event brand.",
  },
  {
    question: "What music does Eastenderz play?",
    answer: "Eastenderz focuses on deep tech, minimal house, dub, garage, tech house and groove-led underground house music.",
  },
  {
    question: "Where is Eastenderz Ibiza taking place?",
    answer: "Eastenderz takes place at Hi Ibiza, with Eastenderz in the Theatre and Paco Osuna's NOW HERE in the Club Room.",
  },
  {
    question: "Who plays at the Eastenderz opening party?",
    answer: "The 2026 opening party includes East End Dubs B2B Skepta, SOSA B2B Hot Since 82, Locklead and Cristina Lazic.",
  },
  {
    question: "What are the biggest Eastenderz Ibiza dates in 2026?",
    answer: "Key dates include the 2 June opening party, 30 June with East End Dubs B2B Jamie Jones, 28 July with Skream and TSHA, 15 September with East End Dubs B2B Marco Carola, and the 6 October closing party.",
  },
  {
    question: "Can I book hotels and tickets through HLT?",
    answer: "Yes. HLT can direct you to Eastenderz ticket links, Ibiza hotel options, concierge support, transfers and full Ibiza trip planning.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "East End Dubs Presents Eastenderz at Hi Ibiza 2026: The Tuesday Residency Built for Deep Tech and Minimal House Fans",
  description:
    "East End Dubs presents Eastenderz at Hi Ibiza every Tuesday from 2 June to 6 October 2026. Discover lineups, key dates, tickets, hotels and HLT Ibiza concierge support for deep tech and minimal house fans.",
  mainEntityOfPage: ARTICLE_URL,
  datePublished: "2026-05-26",
  dateModified: "2026-05-26",
  author: { "@type": "Organization", name: "Hey Let's Travel" },
  publisher: { "@type": "Organization", name: "Hey Let's Travel" },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

function ConciergeCta({ children = "Join the HLT Ibiza Concierge" }) {
  return (
    <Link to={CONCIERGE_URL} className="btn-primary">
      {children} -&gt;
    </Link>
  );
}

function ArticleSection({ title, children }) {
  return (
    <section className="space-y-4 border-t border-white/10 pt-8">
      <h2 className="font-display text-4xl leading-none text-white md:text-5xl">{title}</h2>
      <div className="space-y-4 text-base leading-8 text-slate-300">{children}</div>
    </section>
  );
}

export default function EastenderzHiIbizaArticle() {
  return (
    <>
      <Helmet>
        <title>Eastenderz Hi Ibiza 2026 Tickets | East End Dubs Tuesday Residency</title>
        <meta
          name="description"
          content="East End Dubs presents Eastenderz at Hi Ibiza every Tuesday from 2 June to 6 October 2026. Discover lineups, key dates, tickets, hotels and HLT Ibiza concierge support for deep tech and minimal house fans."
        />
        <meta
          name="keywords"
          content="Eastenderz Ibiza 2026, East End Dubs Ibiza 2026, Eastenderz Hi Ibiza tickets, Hi Ibiza Tuesday 2026, East End Dubs Hi Ibiza, deep tech Ibiza 2026, minimal house Ibiza 2026, Ibiza house music events 2026, Ibiza tech house events 2026, Eastenderz opening party Ibiza, Eastenderz closing party Ibiza, Paco Osuna NOW HERE Hi Ibiza, best Tuesday Ibiza parties, Ibiza party holiday 2026, HLT Ibiza concierge, Ibiza hotels for Hi Ibiza, Ibiza tickets and hotels package"
        />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Eastenderz Hi Ibiza 2026 Tickets | East End Dubs Tuesday Residency" />
        <meta property="og:description" content="Eastenderz lands at Hi Ibiza every Tuesday from 2 June to 6 October 2026. Plan tickets, hotels and concierge support with HLT." />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Eastenderz Hi Ibiza 2026 Tickets" />
        <meta name="twitter:description" content="Plan East End Dubs presents Eastenderz at Hi Ibiza with HLT tickets, hotels and concierge support." />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <article className="page space-y-10">
        <header className="border border-white/10 bg-[#111111] p-6 md:p-8">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-500">Community / Ibiza 2026</p>
          <h1 className="mt-4 max-w-5xl font-display text-5xl leading-none text-white md:text-7xl">
            East End Dubs Presents Eastenderz at Hi Ibiza 2026
          </h1>
          <p className="mt-5 max-w-4xl text-lg leading-8 text-slate-300">
            The Tuesday residency built for deep tech, minimal house, garage, dub and groove-led underground house fans.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <ConciergeCta children="Plan Eastenderz with HLT" />
            <Link to={EVENTS_URL} className="btn-secondary">Browse Ibiza events</Link>
          </div>
        </header>

        <div className="mx-auto max-w-4xl space-y-10">
          <section className="space-y-4 text-base leading-8 text-slate-300">
            <p>
              Ibiza 2026 has a new Tuesday night focus. For house music fans who prefer groove, tension, minimal drums and proper underground energy, East End Dubs presents Eastenderz at Hi Ibiza is one of the most important residencies of the summer.
            </p>
            <p>
              Running every Tuesday from 2 June to 6 October 2026, Eastenderz takes over Hi Ibiza with a full-season residency led by East End Dubs, the London-based DJ, producer and creative force behind one of the most respected names in the deep tech and minimal house scene.
            </p>
            <p>
              This is exactly the kind of Ibiza event HLT is built around. Not random nightlife. Not a cheap party holiday. Not a last-minute club night picked because it happens to be nearby. Eastenderz is for people who travel for the music.
            </p>
            <ConciergeCta children="Ask HLT for Eastenderz tickets and travel" />
          </section>

          <ArticleSection title="Why Eastenderz at Hi Ibiza matters in 2026">
            <p>
              Eastenderz has become a serious name in underground house music. Founded by East End Dubs, the brand has grown from a London-rooted label and event concept into a global movement with a clear sound: deep tech, minimal house, dub, garage and rolling groove.
            </p>
            <p>
              That sound has built a loyal following because it does not rely on obvious drops or commercial moments. It is built on rhythm, detail, tension and connection with the dancefloor.
            </p>
            <p>
              In 2026, Eastenderz brings that identity to Hi Ibiza every Tuesday, giving the brand a full weekly home on the island.
            </p>
          </ArticleSection>

          <ArticleSection title="East End Dubs: the sound behind Eastenderz">
            <p>
              East End Dubs is one of the most respected names in the deep tech and minimal house world. His sound is stripped-back but powerful: rolling basslines, tight percussion, dubby textures, garage influence and club-focused grooves.
            </p>
            <p>
              Through Eastenderz, he has created more than just a label. He has built a community around a specific feeling on the dancefloor. That is why the Hi Ibiza residency feels like such a big moment.
            </p>
          </ArticleSection>

          <ArticleSection title="Eastenderz Hi Ibiza 2026 dates">
            <p>
              Eastenderz runs every Tuesday at Hi Ibiza from Tuesday 2 June 2026 to Tuesday 6 October 2026. For travellers, Tuesday is a perfect anchor night.
            </p>
            <ul className="list-disc space-y-2 pl-6 text-slate-200">
              <li>Monday to Thursday for a focused midweek Ibiza trip</li>
              <li>Tuesday to Friday for Eastenderz plus extra club nights</li>
              <li>Saturday to Wednesday for a longer group holiday with Eastenderz as the main event</li>
              <li>Full week Ibiza package with tickets, hotels, transfers and concierge support</li>
            </ul>
            <ConciergeCta />
          </ArticleSection>

          <ArticleSection title="Opening party: Eastenderz at Hi Ibiza, 2 June 2026">
            <p>The Eastenderz opening party takes place on Tuesday 2 June 2026. The opening night lineup includes:</p>
            <ul className="grid gap-2 border-l border-brand-500/40 pl-5 text-white">
              <li>East End Dubs B2B Skepta</li>
              <li>SOSA B2B Hot Since 82</li>
              <li>Locklead</li>
              <li>Cristina Lazic</li>
            </ul>
            <p>
              In the Club Room, Paco Osuna presents NOW HERE, adding another serious layer to Tuesday nights at Hi Ibiza. For anyone looking at Ibiza in early June, this is a strong date to build around.
            </p>
            <ConciergeCta children="Plan the opening party with HLT" />
          </ArticleSection>

          <ArticleSection title="Key Eastenderz Ibiza 2026 dates to watch">
            <p>The full season is strong, but certain Tuesdays stand out for house music fans planning their Ibiza trip.</p>
            <div className="grid gap-3">
              {KEY_DATES.map((date) => (
                <div key={date} className="border border-white/10 bg-white/[0.03] p-4 text-slate-200">{date}</div>
              ))}
            </div>
          </ArticleSection>

          <ArticleSection title="Why Tuesday nights at Hi Ibiza are worth planning around">
            <p>
              Tuesday is no longer just a midweek filler night in Ibiza. With Eastenderz in the Theatre and Paco Osuna's NOW HERE in the Club Room, Hi Ibiza has built a Tuesday programme that speaks directly to serious clubbers.
            </p>
            <p>Some people want deep tech and minimal house, underground DJs, a crowd that knows the music, a proper dancefloor atmosphere and a reason to travel beyond the obvious weekend parties. Eastenderz gives that audience a weekly home.</p>
          </ArticleSection>

          <ArticleSection title="Build your Ibiza trip around Eastenderz">
            <p>
              If you are planning Ibiza in 2026 and Eastenderz is on your list, the smartest move is to build the trip around the Tuesday night first. Start with the ticket, then build the rest of the trip around it.
            </p>
            <p>Through HLT, you can arrange:</p>
            <ul className="list-disc space-y-2 pl-6 text-slate-200">
              <li>Eastenderz tickets</li>
              <li>Ibiza hotels close to Playa d'en Bossa, Ibiza Town and key nightlife areas</li>
              <li>Group villas for friends travelling together</li>
              <li>Airport transfers and club transport</li>
              <li>VIP and concierge support</li>
              <li>Restaurant and beach club planning</li>
              <li>Full Ibiza packages with tickets, hotels and transfers combined</li>
            </ul>
            <div className="flex flex-wrap gap-3">
              <ConciergeCta children="Join the HLT Ibiza Concierge" />
              <Link to={CONCIERGE_URL} className="btn-secondary">Find Ibiza Hotels With HLT</Link>
              <Link to={JOSH_BAKER_URL} className="btn-secondary">Josh Baker You&Me at Amnesia Ibiza 2026</Link>
            </div>
          </ArticleSection>

          <ArticleSection title="Who should go to Eastenderz at Hi Ibiza?">
            <p>
              Eastenderz is ideal for people who love the more underground side of house music: East End Dubs, SOSA, Hot Since 82, Jamie Jones, Patrick Topping, Kolter, Skream, TSHA, Marco Carola, Gaskin, Franky Rizardo, minimal house, deep tech, dub-influenced house, UK tech house and garage-influenced club music.
            </p>
            <p>
              It is also a strong choice for groups who want a premium Ibiza night without drifting into overly commercial territory. If your Ibiza trip is about music first, Eastenderz belongs on the shortlist.
            </p>
          </ArticleSection>

          <ArticleSection title="Best months to visit Ibiza for Eastenderz">
            <p>June is ideal for opening parties, lower accommodation pressure and early-season energy.</p>
            <p>July is perfect for peak summer atmosphere, group trips and major artist dates.</p>
            <p>August is high season, with the island at full intensity and demand for hotels at its highest.</p>
            <p>September is one of the best months for music-focused travellers, with strong lineups and a slightly more experienced crowd.</p>
            <p>October brings closing party energy, including the Eastenderz closing party on 6 October 2026.</p>
          </ArticleSection>

          <ArticleSection title="Where to stay for Eastenderz at Hi Ibiza">
            <p>Hi Ibiza is located in Playa d'en Bossa, one of the island's main nightlife areas. For Eastenderz, the easiest areas to stay are:</p>
            <ul className="list-disc space-y-2 pl-6 text-slate-200">
              <li>Playa d'en Bossa - close to Hi Ibiza, Ushuaia, beach clubs and late-night transport.</li>
              <li>Ibiza Town - restaurants, marina energy, pre-drinks, taxis and a balanced Ibiza experience.</li>
              <li>Talamanca - a more polished hotel stay while still being close to nightlife.</li>
              <li>San Antonio - usually better for budget-conscious travellers, with transport planning needed for Hi Ibiza.</li>
              <li>Private villas - best for groups who want privacy, space, pool days and a premium trip.</li>
            </ul>
            <Link to={CONCIERGE_URL} className="btn-secondary">Find Ibiza Hotels With HLT</Link>
          </ArticleSection>

          <ArticleSection title="Eastenderz at Hi Ibiza 2026: event details">
            <dl className="grid gap-3 border border-white/10 bg-[#111111] p-5 text-slate-300">
              <div><dt className="text-white">Event</dt><dd>East End Dubs presents Eastenderz</dd></div>
              <div><dt className="text-white">Venue</dt><dd>Hi Ibiza</dd></div>
              <div><dt className="text-white">Main Room</dt><dd>Theatre</dd></div>
              <div><dt className="text-white">Club Room</dt><dd>Paco Osuna presents NOW HERE</dd></div>
              <div><dt className="text-white">Season</dt><dd>Every Tuesday from 2 June to 6 October 2026</dd></div>
              <div><dt className="text-white">Opening Party</dt><dd>Tuesday 2 June 2026</dd></div>
              <div><dt className="text-white">Music</dt><dd>Deep tech, minimal house, tech house, dub, garage and groove-led underground house</dd></div>
            </dl>
          </ArticleSection>

          <ArticleSection title="Final word">
            <p>
              Eastenderz at Hi Ibiza is one of the most important new residencies for Ibiza 2026. For fans of deep tech, minimal house, UK tech house, garage-influenced grooves and proper underground dancefloor energy, Tuesday nights at Hi Ibiza should be high on the list.
            </p>
            <p>
              Book the ticket. Sort the hotel. Plan the trip properly. HLT can help you do all three.
            </p>
            <div className="flex flex-wrap gap-3">
              <ConciergeCta children="Plan Eastenderz with HLT" />
              <Link to={CONCIERGE_URL} className="btn-secondary">Search Ibiza Hotels With HLT</Link>
              <Link to={EVENTS_URL} className="btn-secondary">Best House Music Events in Ibiza 2026</Link>
            </div>
          </ArticleSection>

          <section className="border-t border-white/10 pt-8">
            <h2 className="font-display text-4xl leading-none text-white md:text-5xl">FAQ</h2>
            <div className="mt-5 grid gap-3">
              {FAQS.map((item) => (
                <details key={item.question} className="border border-white/10 bg-[#111111] p-4">
                  <summary className="cursor-pointer font-semibold text-white">{item.question}</summary>
                  <p className="mt-3 leading-7 text-slate-300">{item.answer}</p>
                </details>
              ))}
            </div>
          </section>
        </div>
      </article>
    </>
  );
}
